import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';

import {
  ApiError,
  createMilestone,
  deleteMilestone,
  listMilestones,
  rebaselineMilestone,
  updateMilestone,
  type Milestone,
  type MilestoneStatus,
} from '../api.js';
import { formatDate } from '../labels.js';
import { focusClass, useFocusRow } from './cross-links.js';
import {
  RegisterToolbar,
  RowActions,
  matches,
  useRegisterView,
  type RowAction,
} from './register-kit.js';

/**
 * The dates the project is judged on.
 *
 * Three columns, and the whole screen is about the gap between the first two:
 * **Agreed** (the promise), **Expected** (what is now believed), **Happened**.
 *
 * Changing the agreed date is behind its own button and demands a reason,
 * because it is the one operation that can make a late project look on time.
 * A screen that lets it be edited like a name would quietly destroy the only
 * number here worth having.
 */

const STATUS_LABELS: Readonly<Record<MilestoneStatus, string>> = {
  achieved: 'Achieved',
  late: 'Late',
  at_risk: 'At risk',
  on_track: 'On track',
  not_set: 'No dates yet',
};

const STATUS_SYMBOLS: Readonly<Record<MilestoneStatus, string>> = {
  achieved: '✓',
  late: '■',
  at_risk: '▲',
  on_track: '●',
  not_set: '○',
};

const MIN_REASON = 20;

interface Props {
  readonly projectId: string;
  /** Decided by the server. A Client/Consultant and a Site Engineer hold view
   *  only, and a button that answers 403 is a worse screen than no button. */
  readonly can: { readonly write: boolean; readonly edit: boolean; readonly remove: boolean };
}

export function Milestones({ projectId, can }: Props): JSX.Element {
  const [items, setItems] = useState<readonly Milestone[] | null>(null);
  /**
   * Not the same as an empty register.
   *
   * This screen was the SIXTH instance of the bug Day 17 fixed on five others:
   * a refusal set `error` to null and the list to `[]`, so somebody whose role
   * does not include milestones was told "No milestones yet" — which is false,
   * and which stops them asking anyone about it. Found by the screen-state
   * sweep in `permission-states.test.tsx`.
   */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [rebasing, setRebasing] = useState<string | null>(null);
  const view = useRegisterView('agreed');

  const load = useCallback(async () => {
    try {
      setItems(await listMilestones(projectId));
      setDenied(false);
    } catch (caught) {
      // 404 counts as refused: a non-member gets 404 rather than 403 so the
      // project's existence is not confirmed (ADR-004).
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the milestones.');
      setItems([]);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The rows on screen.
   *
   * Filtering lives HERE rather than in `useRegisterView`, so every dependency
   * is visible to `react-hooks/exhaustive-deps`. It runs before the early
   * returns below because hooks must.
   */
  const shown = useMemo(() => {
    if (!items) return [];

    const status = view.chosen.status;
    const filtered = items.filter((m) => {
      if (!matches(m.name, view.query)) return false;
      if (status === 'critical') return m.is_critical;
      if (status && m.status !== status) return false;
      return true;
    });

    // A null date sorts LAST, never first: a milestone nobody has dated is not
    // the most urgent thing on the job.
    const byDate =
      (pick: (m: Milestone) => string | null) =>
      (a: Milestone, b: Milestone): number => {
        const x = pick(a);
        const y = pick(b);
        if (x === y) return 0;
        if (x === null) return 1;
        if (y === null) return -1;
        return x < y ? -1 : 1;
      };

    const compare: Readonly<Record<string, (a: Milestone, b: Milestone) => number>> = {
      agreed: byDate((m) => m.baseline_date),
      expected: byDate((m) => m.forecast_date),
      // Worst first. A milestone with no variance has none to compare.
      variance: (a, b) => (b.variance_days ?? -Infinity) - (a.variance_days ?? -Infinity),
      name: (a, b) => a.name.localeCompare(b.name),
    };

    return [...filtered].sort(compare[view.sortKey] ?? compare.agreed!);
  }, [items, view.query, view.chosen.status, view.sortKey]);

  async function run(work: () => Promise<readonly Milestone[]>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setItems(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Milestones</h3>
      {items && !adding && can.write && (
        <button type="button" className="inline" onClick={() => setAdding(true)}>
          Add milestone
        </button>
      )}
    </div>
  );

  if (denied) {
    return (
      <>
        {head}
        <p className="hint">Your role does not include this project&rsquo;s milestones.</p>
      </>
    );
  }

  if (items === null) {
    return (
      <>
        {head}
        <p aria-live="polite">Loading milestones…</p>
      </>
    );
  }

  return (
    <>
      {head}

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {adding && (
        <AddForm
          busy={busy}
          onCancel={() => {
            setAdding(false);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => createMilestone(projectId, input))) setAdding(false);
          }}
        />
      )}

      {items.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No milestones yet.</strong>
          </p>
          <p>
            These are the dates the job is judged on. The Award stage needs at least one before
            it can be completed.
          </p>
        </div>
      ) : (
        <>
          <RegisterToolbar
            view={view}
            searchLabel="Search milestones"
            searchPlaceholder="Milestone name"
            shown={shown.length}
            total={items.length}
            noun={['milestone', 'milestones']}
            filters={[
              {
                key: 'status',
                label: 'Show',
                options: [
                  { value: 'late', label: 'Late' },
                  { value: 'at_risk', label: 'At risk' },
                  { value: 'on_track', label: 'On track' },
                  { value: 'achieved', label: 'Achieved' },
                  { value: 'critical', label: 'Critical only' },
                ],
              },
            ]}
            sorts={[
              { key: 'agreed', label: 'Agreed date' },
              { key: 'expected', label: 'Expected date' },
              { key: 'variance', label: 'Most late' },
              { key: 'name', label: 'Name' },
            ]}
          />

          {shown.length === 0 ? (
            /*
             * A DIFFERENT empty state from "no milestones yet": this reader has
             * milestones and a filter that excludes all of them.
             */
            <div className="empty">
              <p>
                <strong>No milestone matches that.</strong>
              </p>
              <p>
                <button type="button" className="link-button" onClick={view.clear}>
                  Clear the search and filters
                </button>{' '}
                to see all {items.length}.
              </p>
            </div>
          ) : (
            <div className={`rk-scroll${view.density === 'compact' ? ' rk-compact' : ''}`}>
        <table className="grid rk-cards">
          <caption className="visually-hidden">
            Project milestones, with the agreed date, the expected date and the variance.{' '}
            {shown.length} of {items.length} shown.
          </caption>
          <thead>
            <tr>
              <th scope="col">Milestone</th>
              <th scope="col">Agreed</th>
              <th scope="col">Expected</th>
              <th scope="col">Happened</th>
              <th scope="col">Variance</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((m) =>
              editing === m.id ? (
                <EditRow
                  key={m.id}
                  milestone={m}
                  busy={busy}
                  onCancel={() => setEditing(null)}
                  onSubmit={async (input) => {
                    if (await run(() => updateMilestone(projectId, m.id, input))) setEditing(null);
                  }}
                />
              ) : (
                <MilestoneRow key={m.id} milestone={m}>
                  <td>
                    <span className="ms-symbol" aria-hidden="true">
                      {STATUS_SYMBOLS[m.status]}
                    </span>{' '}
                    {m.name}
                    {m.is_critical && <span className="chip chip-priority-critical">Critical</span>}
                    <span className="tree-code"> {STATUS_LABELS[m.status]}</span>
                  </td>
                  <td data-label="Agreed">{formatDate(m.baseline_date)}</td>
                  <td data-label="Expected">{formatDate(m.forecast_date)}</td>
                  <td data-label="Happened">{formatDate(m.actual_date)}</td>
                  <td
                    data-label="Variance"
                    className={m.variance_days && m.variance_days > 0 ? 'task-overdue' : undefined}
                  >
                    {m.variance_days === null
                      ? '—'
                      : m.variance_days === 0
                        ? 'On the date'
                        : m.variance_days > 0
                          ? `${m.variance_days} days late`
                          : `${-m.variance_days} days early`}
                  </td>
                  <td>
                    {/*
                      "Update" is the repeated act — moving the EXPECTED date as
                      the job changes. Changing the AGREED one is the operation
                      that can make a late project look on time, so it demands a
                      reason and it stays in the menu, never on the row.

                      `primary` is SPREAD rather than passed as `undefined`: the
                      repo runs `exactOptionalPropertyTypes`, so an absent prop
                      and a prop set to undefined are not the same thing.
                    */}
                    <RowActions
                      rowLabel={m.name}
                      {...(can.edit
                        ? {
                            primary: {
                              label: 'Update',
                              disabled: busy,
                              onSelect: () => {
                                setError(null);
                                setRebasing(null);
                                setEditing(m.id);
                              },
                            } satisfies RowAction,
                          }
                        : {})}
                      more={[
                        ...(can.edit
                          ? [
                              {
                                label: 'Change agreed date',
                                disabled: busy,
                                onSelect: () => {
                                  setError(null);
                                  setEditing(null);
                                  setRebasing(rebasing === m.id ? null : m.id);
                                },
                              } satisfies RowAction,
                            ]
                          : []),
                        ...(can.remove
                          ? [
                              {
                                label: 'Delete',
                                danger: true,
                                disabled: busy,
                                onSelect: () =>
                                  void run(() => deleteMilestone(projectId, m.id)),
                              } satisfies RowAction,
                            ]
                          : []),
                      ]}
                    />
                  </td>
                </MilestoneRow>
              ),
            )}
          </tbody>
        </table>
            </div>
          )}
        </>
      )}

      {rebasing && (
        <RebaselineForm
          milestone={items.find((m) => m.id === rebasing)!}
          busy={busy}
          onCancel={() => {
            setRebasing(null);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => rebaselineMilestone(projectId, rebasing, input))) {
              setRebasing(null);
            }
          }}
        />
      )}

      {items.length > 0 && (
        <p className="hint">
          <strong>Agreed</strong> is what was promised and only changes with a reason.{' '}
          <strong>Expected</strong> is what you believe now. The variance between them is what
          the project is measured on.
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * One row, which knows whether a cross-link came looking for it.
 *
 * An activity can be declared to DELIVER a milestone (`06`'s `delivers_to`),
 * and that link points here from the Site section.
 */
function MilestoneRow({
  milestone,
  children,
}: {
  readonly milestone: Milestone;
  readonly children: React.ReactNode;
}): JSX.Element {
  const { focused, ref } = useFocusRow<HTMLTableRowElement>(milestone.id);
  return (
    <tr
      ref={ref}
      className={focusClass(focused, `ms-${milestone.status}`)}
      aria-current={focused ? 'true' : undefined}
    >
      {children}
    </tr>
  );
}

// ---------------------------------------------------------------------------

interface AddProps {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    name: string;
    baseline_date: string | null;
    is_critical: boolean;
  }) => void | Promise<void>;
}

function AddForm({ busy, onCancel, onSubmit }: AddProps): JSX.Element {
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [critical, setCritical] = useState(false);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!name.trim()) return;
    void onSubmit({ name: name.trim(), baseline_date: date || null, is_critical: critical });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field-row">
        <div className="field">
          <label htmlFor="ms-name">Milestone</label>
          <input
            id="ms-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            disabled={busy}
            placeholder="M1 Site handover"
          />
        </div>
        <div className="field">
          <label htmlFor="ms-date">Agreed date</label>
          <input
            id="ms-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={busy}
          />
          <p className="hint">What was promised. Changing it later needs a reason.</p>
        </div>
      </div>

      <div className="check">
        <input
          id="ms-critical"
          type="checkbox"
          checked={critical}
          disabled={busy}
          onChange={(e) => setCritical(e.target.checked)}
        />
        <label htmlFor="ms-critical">
          Critical
          <span className="hint">{' · '}A slip here counts against the project's health.</span>
        </label>
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !name.trim()}>
          {busy ? 'Adding…' : 'Add milestone'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

interface EditProps {
  readonly milestone: Milestone;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    forecast_date: string | null;
    actual_date: string | null;
    is_critical: boolean;
  }) => void | Promise<void>;
}

/**
 * The everyday edit: what you now expect, and what actually happened.
 *
 * There is no field for the agreed date here, deliberately — the API refuses it
 * too, so the two agree about what this form is for.
 */
function EditRow({ milestone, busy, onCancel, onSubmit }: EditProps): JSX.Element {
  const [forecast, setForecast] = useState(milestone.forecast_date ?? '');
  const [actual, setActual] = useState(milestone.actual_date ?? '');
  const [critical, setCritical] = useState(milestone.is_critical);

  return (
    <tr className="editing">
      <td>{milestone.name}</td>
      <td data-label="Agreed">
        {formatDate(milestone.baseline_date)}
        <span className="hint">{' · '}Not editable here</span>
      </td>
      <td data-label="Expected">
        <label className="visually-hidden" htmlFor={`fc-${milestone.id}`}>
          Expected date for {milestone.name}
        </label>
        <input
          id={`fc-${milestone.id}`}
          className="tree-input"
          type="date"
          value={forecast}
          onChange={(e) => setForecast(e.target.value)}
          disabled={busy}
        />
      </td>
      <td data-label="Happened">
        <label className="visually-hidden" htmlFor={`ac-${milestone.id}`}>
          Date it happened for {milestone.name}
        </label>
        <input
          id={`ac-${milestone.id}`}
          className="tree-input"
          type="date"
          value={actual}
          onChange={(e) => setActual(e.target.value)}
          disabled={busy}
        />
      </td>
      <td data-label="Variance">
        <div className="check compact">
          <input
            id={`cr-${milestone.id}`}
            type="checkbox"
            checked={critical}
            disabled={busy}
            onChange={(e) => setCritical(e.target.checked)}
          />
          <label htmlFor={`cr-${milestone.id}`}>Critical</label>
        </div>
      </td>
      <td className="row-actions">
        <button
          type="button"
          className="link-button"
          disabled={busy}
          onClick={() =>
            void onSubmit({
              forecast_date: forecast || null,
              actual_date: actual || null,
              is_critical: critical,
            })
          }
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="link-button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------

interface RebaseProps {
  readonly milestone: Milestone;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: { baseline_date: string; reason: string }) => void | Promise<void>;
}

function RebaselineForm({ milestone, busy, onCancel, onSubmit }: RebaseProps): JSX.Element {
  const [date, setDate] = useState(milestone.baseline_date ?? '');
  const [reason, setReason] = useState('');

  return (
    <div className="panel">
      <p className="panel-title">Change the agreed date for “{milestone.name}”</p>
      <p className="hint">
        This is the date the job is measured against. Moving it makes a late milestone look on
        time, so it is recorded permanently with your reason — an instruction, a variation, an
        agreed extension.
      </p>

      <div className="field-row">
        <div className="field">
          <label htmlFor="rb-date">New agreed date</label>
          <input
            id="rb-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="rb-reason">Why is it moving?</label>
        <textarea
          id="rb-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          maxLength={2000}
          disabled={busy}
          placeholder="Client instructed a four-week delay to the handover date."
        />
        <p className="hint">
          {reason.trim().length}/{MIN_REASON} characters minimum.
        </p>
      </div>

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !date || reason.trim().length < MIN_REASON}
          onClick={() => void onSubmit({ baseline_date: date, reason: reason.trim() })}
        >
          {busy ? 'Saving…' : 'Change the agreed date'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
